import React, { useEffect, useMemo, useState } from 'react';
import QuillEditor from './shared/QuillEditor';
import Select from 'react-select';
import { fetchTags, attachTagApi, detachTagApi, fetchUsers, updateProjectTaskApi, uploadAttachmentToBlob, createProjectTaskApi } from './api';

const TaskModal = ({ isOpen, onClose, task, projectName, onSaved, mode = 'edit', defaultProjectId }) => {
  const [title, setTitle] = useState(task?.title || '');
  const [status, setStatus] = useState(task?.status || 'todo');
  const [priority, setPriority] = useState(task?.priority || 'medium');
  const [assigneeId, setAssigneeId] = useState(task?.assignee_id || null);
  const [description, setDescription] = useState(task?.description || '');
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState(Array.isArray(task?.tags) ? task.tags : []);
  const [users, setUsers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(task?.title || '');
    setStatus(task?.status || 'todo');
    setPriority(task?.priority || 'medium');
    setAssigneeId(task?.assignee_id || null);
    setDescription(task?.description || '');
    setSelectedTagIds(Array.isArray(task?.tags) ? task.tags : []);
    setAttachments([]);
    setError(null);
  }, [isOpen, task]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const [t, u] = await Promise.all([fetchTags(), fetchUsers()]);
        setTags(t);
        setUsers(u);
      } catch (_) {}
    })();
  }, [isOpen]);

  const tagOptions = useMemo(() => tags.map(t => ({ value: t.id, label: t.name, color: t.color })), [tags]);

  const handleFileSelect = (files) => {
    const list = Array.from(files).map(file => ({ id: Math.random().toString(36).slice(2), file, name: file.name, size: file.size, type: file.type }));
    setAttachments(prev => [...prev, ...list]);
  };

  const removeAttachment = (id) => setAttachments(prev => prev.filter(a => a.id !== id));

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      if (attachments.length) {
        const uploaded = await Promise.all(attachments.map(a => uploadAttachmentToBlob(a.file, 'data-dumps')));
        const links = uploaded.map(u => `\n<a href="${u.url}" target="_blank" rel="noreferrer">${u.blobName || u.url}</a>`).join('');
        setDescription(prev => prev + links);
      }
      if (mode === 'create' || !task?.id) {
        const created = await createProjectTaskApi({
          project_id: defaultProjectId,
          title,
          status,
          priority,
          assignee_id: assigneeId || null,
          description
        });
        const next = new Set(selectedTagIds);
        await Promise.all(Array.from(next).map(id => attachTagApi({ target_type: 'task', target_id: created.id, tag_id: id })));
        onSaved && onSaved({ ...created, tags: Array.from(next) });
      } else {
        const updated = await updateProjectTaskApi(task.id, {
          title,
          status,
          priority,
          assignee_id: assigneeId || null,
          description
        });
        const current = new Set(Array.isArray(task?.tags) ? task.tags : []);
        const next = new Set(selectedTagIds);
        const toAttach = Array.from(next).filter(id => !current.has(id));
        const toDetach = Array.from(current).filter(id => !next.has(id));
        await Promise.all([
          ...toAttach.map(id => attachTagApi({ target_type: 'task', target_id: task.id, tag_id: id })),
          ...toDetach.map(id => detachTagApi({ target_type: 'task', target_id: task.id, tag_id: id }))
        ]);
        onSaved && onSaved({ ...updated, tags: Array.from(next) });
      }
      onClose && onClose();
    } catch (e) {
      setError(e.message || 'Failed to save task');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">{(mode === 'create' || !task?.id) ? 'Create Task' : 'Edit Task'} {projectName ? `– ${projectName}` : ''}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input value={title} onChange={(e)=>setTitle(e.target.value)} className="w-full p-2 border rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={status} onChange={(e)=>setStatus(e.target.value)} className="w-full p-2 border rounded-md">
              <option value="todo">todo</option>
              <option value="in_progress">in_progress</option>
              <option value="done">done</option>
              <option value="blocked">blocked</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select value={priority} onChange={(e)=>setPriority(e.target.value)} className="w-full p-2 border rounded-md">
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Responsible</label>
            <Select
              options={users.map(u => ({ value: u.id, label: u.email || u.name || `User ${u.id}` }))}
              value={users.map(u => ({ value: u.id, label: u.email || u.name || `User ${u.id}` })).find(o => o.value === assigneeId) || null}
              onChange={(opt)=>setAssigneeId(opt?.value || null)}
              isClearable
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <div className="border rounded-md">
            <QuillEditor value={description} onChange={setDescription} placeholder="Add task details..." height="160px" />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
          <Select
            isMulti
            options={tagOptions}
            value={tagOptions.filter(o => selectedTagIds.includes(o.value))}
            onChange={(selected)=>setSelectedTagIds((selected||[]).map(s=>s.value))}
            classNamePrefix="react-select"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Attachments</label>
          <div className="border-2 border-dashed rounded-lg p-2 text-center">
            <label className="text-blue-600 hover:text-blue-800 cursor-pointer">
              browse
              <input type="file" multiple className="hidden" onChange={(e)=>handleFileSelect(e.target.files)} />
            </label>
          </div>
          {attachments.length>0 && (
            <div className="mt-2 space-y-2">
              {attachments.map(a => (
                <div key={a.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                  <div className="text-sm">{a.name} <span className="text-xs text-gray-500">({(a.size/1024/1024).toFixed(2)} MB)</span></div>
                  <button onClick={()=>removeAttachment(a.id)} className="text-xs text-red-600">Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded" disabled={isSaving}>Cancel</button>
          <button onClick={handleSave} className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded" disabled={isSaving}>{isSaving? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;


